import { useState } from 'react';
import { Checkbox, Container, Header, Segment } from 'semantic-ui-react';
import { Navigate } from 'react-router-dom';

import { ResponsiveContainer } from '../../ResponsiveContainer';
import { PATHS } from '../../../paths';
import { useCanCreatePersonLoading } from '../../../hooks/canCreatePerson';

import { ClassicPersonCreator } from './ClassicPersonCreator';
import { NewPersonCreator } from './NewPersonCreator';
import { REGISTRATION_TEXT, RegistrationLangProvider, type RegistrationLang } from './registrationText';

import './PersonCreator.css';

/**
 * "Add person", in two forms.
 *
 * The classic form is the one Centre Managers use every day; the new registration form captures
 * consent, guardian details for minors and supporting documents. **Both are live at once**,
 * chosen by the toggle in the corner of the card, because changing a daily data-entry workflow
 * mid-season is how you lose a season's data quality.
 *
 * They share one page shell and one `adminCreateUser` mutation — the registration arguments are
 * all optional server-side and the classic form simply sends none of them. That is what stops
 * the two from drifting into separate code paths that have to be fixed twice.
 */

/**
 * Per browser, per person. `localStorage` rather than a user setting on purpose: this is a
 * preference about *how someone types*, it costs nothing to re-pick, and a round trip to the
 * server for it would be a column, a mutation and a migration.
 */
const STORAGE_KEY = 'joyya.personCreator.newForm';

/** Same reasoning: which script someone likes the form in is a per-browser habit. */
const LANG_STORAGE_KEY = 'joyya.personCreator.lang';

const readPreference = (): boolean => {
    try {
        return window.localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
        // Private browsing, or storage disabled. Fall back to the classic form — the safe
        // default is the one that was already there.
        return false;
    }
};

const readLang = (): RegistrationLang => {
    try {
        return window.localStorage.getItem(LANG_STORAGE_KEY) === 'bn' ? 'bn' : 'en';
    } catch {
        return 'en';
    }
};

const PersonCreator = () => {
    const [useNewForm, setUseNewForm] = useState<boolean>(readPreference);
    // Both only mean anything on the new form. The classic form renders exactly as it did.
    const [lang, setLang] = useState<RegistrationLang>(readLang);
    const [editing, setEditing] = useState(false);
    const [canCreate, gateLoading] = useCanCreatePersonLoading();

    const toggle = (checked: boolean) => {
        setUseNewForm(checked);
        try {
            window.localStorage.setItem(STORAGE_KEY, String(checked));
        } catch {
            // Not being able to remember the choice is not a reason to refuse making it.
        }
    };

    const toggleLang = (bengali: boolean) => {
        const next: RegistrationLang = bengali ? 'bn' : 'en';
        setLang(next);
        try {
            window.localStorage.setItem(LANG_STORAGE_KEY, next);
        } catch {
            // As above — not remembering is not a reason to refuse.
        }
    };

    // The new form's words; the classic form keeps its own English heading untouched.
    const text = REGISTRATION_TEXT[useNewForm ? lang : 'en'];

    if (gateLoading) {
        return <ResponsiveContainer><Container><p>Loading…</p></Container></ResponsiveContainer>;
    }

    // **The page has never had a gate.** Only the menu entry was hidden, so `/people/create`
    // was reachable by anyone who typed it and failed at the mutation — after they had filled
    // the form in. Now that the gate is one server answer rather than a role test repeated per
    // resolver, the page can ask the same question the mutation will.
    if (!canCreate) {
        return <Navigate to={PATHS.PEOPLE} replace />;
    }

    return (
        <ResponsiveContainer>
            <Segment style={{ padding: '1em 0em' }} vertical>
                <Container className="person-creator">
                    <div className="creator-card">
                        <div className="creator-top">
                            <Header as="h2" className="creator-header" lang={useNewForm ? lang : undefined}>
                                {useNewForm && editing ? text.editPerson : text.addPerson}
                            </Header>
                            <div>
                                <Checkbox
                                    toggle
                                    // Semantic only emits `id`/`htmlFor` when given an id; without
                                    // one the switch has no accessible name.
                                    id="person-creator-form-switch"
                                    className="form-switch"
                                    label={text.newFormSwitch}
                                    checked={useNewForm}
                                    onChange={(_, data) => {
                                        toggle(!!data.checked);
                                        if (!data.checked) setEditing(false);
                                    }}
                                />
                                {/* Labelled in Bengali whichever way it is set, so someone who reads
                                    only Bengali can find it. */}
                                {useNewForm && (
                                    <Checkbox
                                        toggle
                                        id="person-creator-bengali-switch"
                                        className="form-switch"
                                        label="বাংলা"
                                        lang="bn"
                                        checked={lang === 'bn'}
                                        onChange={(_, data) => toggleLang(!!data.checked)}
                                        style={{ marginLeft: '1.5em' }}
                                    />
                                )}
                            </div>
                        </div>

                        {useNewForm ? (
                            <RegistrationLangProvider value={lang}>
                                <div lang={lang}>
                                    <NewPersonCreator onEditingChange={setEditing} />
                                </div>
                            </RegistrationLangProvider>
                        ) : <ClassicPersonCreator />}
                    </div>
                </Container>
            </Segment>
        </ResponsiveContainer>
    );
};

export { PersonCreator };
